/**
 * Which deck, or which table — chosen from a list you can see.
 *
 * These were segmented buttons, which is the right control for two or three
 * options and falls apart at eleven: the row ran off the screen, and every
 * option was a word. A deck called "Nevermore" tells you nothing about what it
 * looks like, and what it looks like is the entire reason somebody bought it.
 *
 * So: a button showing what is on now, and a list showing what else there is,
 * each row carrying the picture. Not a `<select>`, which cannot hold an image
 * — and the image is the point rather than an embellishment.
 *
 * ## Why the preview and not a swatch
 *
 * A colour chip would say "this one is green" about a deck whose value is a
 * painted knight. The row shows the same table crop the store sells it with,
 * so what you chose it from and what you are choosing between look the same.
 */

import { useEffect, useRef, useState } from 'react';
import { Icon } from '../render/Icon.js';

export interface ThemeChoice {
  /** The id this picker sets — a deck id or a table id. */
  id: string;
  name: string;
  /** The theme's deck, for drawing cards. */
  deckId: string;
  /** The theme's table, for drawing the room. Named separately because
      Classic's table is "default" while its deck is "classic". */
  tableId: string;
}

/**
 * What a row shows: the thing being chosen, not the theme in general.
 *
 * The Cards picker showed a picture of a table, which is the wrong question
 * answered confidently - somebody choosing a deck is asking what the cards
 * look like. Two cards, a face and a back, side by side: the back is what
 * makes a deck recognisable across the table, and the face carries the paper
 * and the pips.
 */
function Preview({ kind, choice }: { kind: 'cards' | 'table'; choice: ThemeChoice }) {
  if (kind === 'table') {
    return (
      <span
        className="theme-swatch"
        style={{ backgroundImage: `url("/art/table-${choice.tableId}-preview.webp")` }}
        aria-hidden="true"
      />
    );
  }

  /* Classic has no card art at all - it is drawn from text - so it gets the
     card shapes the stylesheet paints rather than two broken images. */
  if (choice.deckId === 'classic') {
    return (
      <span className="theme-swatch cards" aria-hidden="true">
        <span className="theme-swatch-card classic-face" />
        <span className="theme-swatch-card classic-back" />
      </span>
    );
  }

  return (
    <span className="theme-swatch cards" aria-hidden="true">
      <img className="theme-swatch-card" src={`/art/deck/${choice.deckId}/AS.webp`} alt="" loading="lazy" />
      <img className="theme-swatch-card" src={`/art/deck/${choice.deckId}/back.webp`} alt="" loading="lazy" />
    </span>
  );
}

export function ThemePicker({
  label,
  help,
  preview,
  options,
  current,
  busy,
  onPick,
}: {
  label: string;
  help: string;
  /** Which half of a theme this picker is for, and therefore what it draws. */
  preview: 'cards' | 'table';
  options: ThemeChoice[];
  current: string;
  busy: boolean;
  onPick: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);

  /*
   * Closes on a press anywhere else, and on Escape.
   *
   * Opened by accident this covers the field under it, and a list whose only
   * way out is to choose something is a list that makes you choose something.
   */
  useEffect(() => {
    if (!open) return undefined;
    const away = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', away);
    window.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('mousedown', away);
      window.removeEventListener('keydown', key);
    };
  }, [open]);

  const chosen = options.find((option) => option.id === current) ?? options[0];

  return (
    <div className="field" ref={box}>
      <span className="field-label">{label}</span>

      <button
        type="button"
        className="theme-select"
        disabled={busy}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {chosen ? <Preview kind={preview} choice={chosen} /> : null}
        <span className="theme-select-name">{chosen?.name ?? '—'}</span>
        {/* The one chevron we own, turned a quarter: this opens a list rather
            than leading somewhere. */}
        <Icon name="chevron-right" className="theme-select-mark" />
      </button>

      {open ? (
        <ul className="theme-options" role="listbox" aria-label={label}>
          {options.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                role="option"
                aria-selected={option.id === current}
                className={`theme-option${option.id === current ? ' chosen' : ''}`}
                onClick={() => {
                  setOpen(false);
                  if (option.id !== current) onPick(option.id);
                }}
              >
                <Preview kind={preview} choice={option} />
                <span className="theme-select-name">{option.name}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <span className="field-help">{help}</span>
    </div>
  );
}
